import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ChevronDown, ChevronRight, CircleCheck, CircleX, Clock, History, NotebookPen, Play, Plus, Target, Trash2, TrendingDown, TrendingUp,
  TriangleAlert, Trophy,
} from 'lucide-react';
import { examService, apiErrorMessage } from '../services/api';
import { useCourse } from '../contexts/CourseContext';
import {
  AppShell, Badge, Button, Card, EmptyState, Loader, Modal, PageHeader, ScoreCard, ScoreTrend, SectionTitle,
} from '../components/ui';
import './ExamHistory.css';

const TOPICS_PREVIEW = 6;

const STYLE_LABEL = {
  fiap: 'Estudo de caso', review: 'Revisão rápida', mixed: 'Misto', errors: 'Meus erros', weak: 'Tópicos fracos',
};

const fmtScore = (v) => (v == null ? '—' : `${Math.round(Number(v))}%`);
const fmtDate = (iso) => new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const fmtMinutes = (s) => (s == null ? null : `${Math.max(1, Math.round(s / 60))} min`);

/** Simulados do curso: em andamento (retomar ou descartar), evolução, desempenho por tópico e histórico. */
function ExamHistory() {
  const { courseSlug } = useParams();
  const navigate = useNavigate();
  const { currentCourse, selectCourse } = useCourse();
  const [data, setData] = useState(null);
  const [discard, setDiscard] = useState(null);
  const [error, setError] = useState(null);
  const [allTopics, setAllTopics] = useState(false);

  const buildPath = (path) => `/curso/${courseSlug}${path}`;

  useEffect(() => { selectCourse(courseSlug); }, [courseSlug, selectCourse]);

  const load = useCallback(async () => {
    const [stats, completed, pending] = await Promise.all([
      examService.getStats(courseSlug),
      examService.getCompletedExams(courseSlug),
      examService.getPendingExams(courseSlug),
    ]);
    setData({ stats, completed, pending });
  }, [courseSlug]);

  useEffect(() => {
    load().catch(err => setError(apiErrorMessage(err)));
  }, [load]);

  const confirmDiscard = async () => {
    const exam = discard;
    setDiscard(null);
    try {
      await examService.discardExam(exam.id);
      load();
    } catch (err) {
      setError(apiErrorMessage(err));
    }
  };

  if (!data) {
    return (
      <AppShell section="Simulados" courseName={currentCourse?.name}>
        {error ? <EmptyState file="simulados.xml" title="Não foi possível carregar" description={error} /> : <Loader />}
      </AppShell>
    );
  }

  const { stats, completed, pending } = data;
  const weakCount = stats.topics.filter(t => t.accuracy < 0.7).length;
  const trend = stats.evolution.length >= 2
    ? Number(stats.evolution[stats.evolution.length - 1].score) - Number(stats.evolution[stats.evolution.length - 2].score)
    : null;
  const passing = Math.round(Number(stats.passingPercentage));
  const average = stats.averageScore == null ? 0 : Math.round(Number(stats.averageScore));
  // Só o simulado iniciado mais recentemente ganha o "Retomar" sólido
  const latestPendingId = pending.reduce(
    (best, p) => (!best || new Date(p.createdAt) > new Date(best.createdAt) ? p : best), null,
  )?.id;
  const sortedTopics = [...stats.topics].sort((a, b) => a.accuracy - b.accuracy);
  // Os mais fracos primeiro; a lista completa fica atrás de um toggle para o card não esticar a linha
  const visibleTopics = allTopics ? sortedTopics : sortedTopics.slice(0, TOPICS_PREVIEW);

  return (
    <AppShell section="Simulados" courseName={currentCourse?.name}>
      <div className="ss-page">
        <PageHeader
          icon={Target}
          eyebrow={currentCourse?.name}
          title="Simulados"
          subtitle="Retome os simulados em andamento, acompanhe sua evolução e descubra onde focar."
          actions={<Button variant="accent" icon={Plus} onClick={() => navigate(buildPath('/config'))} data-tour="novo-simulado">Novo simulado</Button>}
        />

        {error && <div className="eh-error" role="alert">{error}</div>}

        {pending.length > 0 && (
          <section className="eh-block" data-tour="em-andamento">
            <SectionTitle
              icon={Clock}
              iconTone="warning"
              aside={`${pending.length} ${pending.length === 1 ? 'simulado' : 'simulados'}`}
            >
              Em andamento
            </SectionTitle>
            <Card padding="none" className="eh-pending">
              {pending.map(p => {
                const latest = p.id === latestPendingId;
                const pct = p.totalQuestions ? (p.answered / p.totalQuestions) * 100 : 0;
                return (
                  <div key={p.id} className="eh-pending__row">
                    <div className="eh-pending__info">
                      <strong>{p.title}</strong>
                      <span>
                        iniciado em {fmtDate(p.createdAt)}
                        {p.remainingSeconds != null && ` · ${fmtMinutes(p.remainingSeconds)} restantes`}
                      </span>
                    </div>
                    <div className="eh-pending__progress">
                      <span className="eh-pending__track" aria-hidden="true">
                        <span style={{ width: `${pct}%` }} />
                      </span>
                      <span>{p.answered}/{p.totalQuestions} respondidas</span>
                    </div>
                    <div className="eh-pending__actions">
                      <button
                        type="button"
                        className="eh-pending__discard"
                        title="Descartar"
                        aria-label={`Descartar ${p.title}`}
                        onClick={() => setDiscard(p)}
                      >
                        <Trash2 size={16} aria-hidden="true" />
                      </button>
                      <Button
                        variant={latest ? 'primary' : 'outline'}
                        icon={Play}
                        onClick={() => navigate(buildPath(`/exam/${p.id}`))}
                      >
                        Retomar
                      </Button>
                    </div>
                  </div>
                );
              })}
            </Card>
          </section>
        )}

        {completed.length === 0 ? (
          <section className="ss-section">
            <SectionTitle icon={History}>Histórico</SectionTitle>
            <EmptyState
              file="historico.xml"
              title="Nenhum simulado finalizado ainda"
              description="Faça o primeiro para acompanhar sua evolução e ver o desempenho por tópico."
              action={<Button icon={Plus} onClick={() => navigate(buildPath('/config'))}>Novo simulado</Button>}
            />
          </section>
        ) : (
          <>
            <section className={pending.length > 0 ? 'ss-section' : 'eh-block'}>
              <SectionTitle icon={TrendingUp}>Desempenho</SectionTitle>
              <ScoreCard
                tour="media"
                top={(
                  <div className="eh-avg">
                    <div className="eh-avg__text">
                      <span className="ss-scorehero__eyebrow">Média geral</span>
                      <span className="ss-scorehero__value eh-avg__value">{fmtScore(stats.averageScore)}</span>
                      <span className="eh-avg__hint">aprovação a partir de {fmtScore(stats.passingPercentage)}</span>
                    </div>
                    <div className="eh-avg__scale">
                      <div className="ss-scorehero__bar">
                        <span className="ss-scorehero__fill" style={{ width: `${Math.min(100, Math.max(0, average))}%` }} />
                        <span className="ss-scorehero__marker" style={{ left: `${passing}%` }} />
                      </div>
                      <div className="eh-avg__ticks" aria-hidden="true">
                        <span>0</span>
                        <span className="eh-avg__goal" style={{ left: `${passing}%` }}>meta {passing}%</span>
                        <span>100</span>
                      </div>
                    </div>
                  </div>
                )}
                items={[
                  { icon: History, value: stats.completedExams, label: 'Finalizados', hint: 'no curso' },
                  { icon: Trophy, value: fmtScore(stats.bestScore), label: 'Melhor nota', hint: 'recorde', tone: 'lime' },
                  {
                    icon: trend != null && trend < 0 ? TrendingDown : TrendingUp,
                    value: fmtScore(stats.lastScore),
                    label: 'Última nota',
                    hint: trend == null ? 'faça mais um para comparar' : `${trend >= 0 ? '+' : ''}${Math.round(trend)} vs. anterior`,
                  },
                  {
                    icon: TriangleAlert,
                    value: weakCount,
                    label: 'Tópicos fracos',
                    hint: 'abaixo de 70%',
                    tone: weakCount > 0 ? 'danger' : 'navy',
                  },
                ]}
              />

              <div className="eh-charts">
                <Card padding="lg" className="eh-chart">
                  <div className="eh-chart__head">
                    <h2>Evolução da nota</h2>
                    <span>clique num ponto</span>
                  </div>
                  <ScoreTrend
                    points={stats.evolution}
                    threshold={passing}
                    onSelect={(p) => navigate(buildPath(`/results/${p.examId}`))}
                  />
                </Card>

                <Card padding="lg" className="eh-chart" data-tour="por-topico">
                  <div className="eh-chart__head">
                    <h2>Acerto por tópico</h2>
                    {weakCount > 0
                      ? <span className="eh-weak"><TriangleAlert size={12} aria-hidden="true" />{weakCount} abaixo de 70%</span>
                      : <span>Todos acima de 70%</span>}
                  </div>
                  <ul className="eh-topics" aria-label="Acerto por tópico">
                    {visibleTopics.map(t => {
                      const pct = Math.round(t.accuracy * 100);
                      const weak = t.accuracy < 0.7;
                      return (
                        <li key={t.categoryId} className={weak ? 'is-weak' : ''}>
                          <div className="eh-topics__head">
                            <span title={t.name}>{t.name}</span>
                            <span><strong>{pct}%</strong> · {t.correct}/{t.total}</span>
                          </div>
                          <span className="eh-topics__track" aria-hidden="true">
                            <span style={{ width: `${Math.max(pct, 1)}%` }} />
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                  {sortedTopics.length > TOPICS_PREVIEW && (
                    <button type="button" className="eh-topics__more" onClick={() => setAllTopics(v => !v)}>
                      {allTopics ? 'Mostrar só os mais fracos' : `Ver todos os ${sortedTopics.length} tópicos`}
                      <ChevronDown size={14} aria-hidden="true" className={allTopics ? 'is-open' : ''} />
                    </button>
                  )}
                  <div className="eh-chart__actions">
                    <Button size="sm" variant="outline" icon={TrendingDown} onClick={() => navigate(buildPath('/config?style=weak'))}>
                      Simulado dos tópicos fracos
                    </Button>
                    <Button size="sm" variant="ghost" icon={NotebookPen} onClick={() => navigate(buildPath('/config?style=errors'))}>
                      Simulado dos meus erros
                    </Button>
                  </div>
                </Card>
              </div>
            </section>

            <section className="ss-section" data-tour="historico">
              <SectionTitle icon={History} aside={`${completed.length} ${completed.length === 1 ? 'simulado' : 'simulados'}`}>
                Histórico
              </SectionTitle>
              <Card padding="none" className="eh-list">
                {completed.map(e => (
                  <button key={e.id} type="button" className="eh-item" onClick={() => navigate(buildPath(`/results/${e.id}`))}>
                    <span className={`eh-item__icon ${e.passed ? 'is-pass' : 'is-fail'}`} aria-hidden="true">
                      {e.passed ? <CircleCheck size={19} /> : <CircleX size={19} />}
                    </span>
                    <span className="eh-item__text">
                      <strong>{e.title}</strong>
                      <span>{fmtDate(e.completedAt)} · {STYLE_LABEL[e.examStyle] || 'Simulado'} · {e.totalQuestions} questões</span>
                    </span>
                    <span className={`eh-item__score ${e.passed ? 'is-pass' : 'is-fail'}`}>{fmtScore(e.score)}</span>
                    <Badge tone={e.passed ? 'success' : 'danger'}>{e.passed ? 'Aprovado' : 'Reprovado'}</Badge>
                    <ChevronRight size={18} className="eh-item__chevron" aria-hidden="true" />
                  </button>
                ))}
              </Card>
            </section>
          </>
        )}
      </div>

      <Modal
        open={!!discard}
        onClose={() => setDiscard(null)}
        title="Descartar simulado?"
        footer={
          <>
            <Button variant="outline" onClick={() => setDiscard(null)}>Manter</Button>
            <Button variant="danger" icon={Trash2} onClick={confirmDiscard}>Descartar</Button>
          </>
        }
      >
        <p>As respostas de <strong>{discard?.title}</strong> serão apagadas e ele não entra no histórico.</p>
      </Modal>
    </AppShell>
  );
}

export default ExamHistory;
